import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { useCompleteModal } from 'modal.react.js';
import { Controller, useForm } from 'react-hook-form';

import { getSampleByItineraryId, getSampleBySampleItineraryId } from 'api/mergeItineraries';
import { SelectSample } from 'components/Pages/Admin/Itinerary/shared/SelectSample/SelectSample';
import { Alert } from 'components/shared/Alert';
import { AModal } from 'components/shared/AModal';
import { FormFieldWrapper } from 'components/shared/BaseFormComponents';
import Button from 'components/shared/Button';
import { InputDatePicker, InputDayPicker } from 'components/shared/HookFormComponents/datePicker/inputs';
import { SNACKBAR_SKIN } from 'components/shared/Snackbar';
import Text from 'components/shared/Typography/Text';
import { showSnackbar } from 'hooks/useSnackbar';

import s from './index.module.css';
import * as t from './index.types';
import { getMergeItineraryValidationSchema } from './validation';

export const MergeItineraryModal = ({
  itineraryId,
  isSample,
  isShowDatePicker = false,
  isShowDayPicker = false,
  onFormSuccess,
}: t.Props) => {
  const modalControl = useCompleteModal();

  const { control, formState, handleSubmit } = useForm<t.FormValues>({
    resolver: yupResolver(getMergeItineraryValidationSchema({ isShowDatePicker, isShowDayPicker })),
    mode: 'onBlur',
  });

  const closeModal = () => {
    modalControl.error(null);
  };

  const submitForm = async () => {
    try {
      await handleSubmit(async (value) => {
        const request = isSample ? getSampleByItineraryId : getSampleBySampleItineraryId;
        const template = await request(itineraryId, value.sampleId);
        await onFormSuccess({
          data: template?.data,
          refereceDate: value?.date?.start,
          referenceDay: value?.day?.start,
        });
        modalControl.success(true);
      })();
    } catch (err: unknown) {
      const error = err as AxiosError;
      let text = 'Oops... Problems with merging itinerary';
      if (error.message) {
        text = text + `: \n${error.message}`;
      }
      showSnackbar({
        text,
        type: SNACKBAR_SKIN.ERROR,
        uniqueId: 'Insert Itinerary',
      });
    }
  };

  return (
    <AModal
      header="Insert Itinerary"
      isOpen={true}
      footer={<MergeItineraryModalFooter isValid={formState.isValid} submitForm={submitForm} />}
      size="auto"
      bodyClassName={s.body}
      onClose={closeModal}
      isCloseOnEsc
    >
      {/* Modal Content */}
      <div className={s.modalContent}>
        {/* Alert block */}
        <div>
          <Alert
            variant="info"
            content={
              <Text>
                Note: you can only insert a calendar itinerary template within a calendar itinerary or a classic
                itinerary template within a classic itinerary.
              </Text>
            }
          />
        </div>

        {/* Title */}
        <div>
          <Text type="brandon">Search your Itinerary Templates.</Text>
        </div>

        {/* Select Itinerary */}
        <div>
          <SelectSample isSample={isSample} itineraryId={itineraryId} name="sampleId" control={control} />
        </div>

        {/* Select Date */}
        {isShowDatePicker && (
          <div>
            <Controller
              name="date.start"
              control={control}
              render={(fieldProps) => {
                return (
                  <FormFieldWrapper errorMessage={fieldProps.fieldState.error?.message}>
                    <InputDatePicker mode="single" placeholder="Select Date" closeOnSelect {...fieldProps} />
                  </FormFieldWrapper>
                );
              }}
            />

            {/* Date subtitle */}
            <Text type="brandon" size="medium" className={s.dateSubtitle}>
              The template days will be inserted starting from the selected date.
            </Text>
          </div>
        )}

        {/* Select Day */}
        {isShowDayPicker && (
          <div>
            <Controller
              name="day"
              control={control}
              render={({ field, ...fieldProps }) => {
                return (
                  <FormFieldWrapper errorMessage={fieldProps.fieldState.error?.message}>
                    <InputDayPicker
                      mode="single"
                      placeholder="Select Day"
                      closeOnSelect
                      field={{
                        ...field,
                        value: field.value?.start as never, // TODO: make single picker in form value
                        onChange: (value) => field.onChange({ start: value, end: value }),
                      }}
                      {...fieldProps}
                    />
                  </FormFieldWrapper>
                );
              }}
            />

            <Text type="brandon" size="medium" className={s.dateSubtitle}>
              The template days will be inserted starting from the selected day.
            </Text>
          </div>
        )}
      </div>
    </AModal>
  );
};

const MergeItineraryModalFooter = ({ isValid, submitForm }: { isValid: boolean; submitForm: () => void }) => {
  return (
    <div>
      <Button onClick={submitForm} disabled={!isValid} type="primary" size="medium" htmlType="button">
        Insert
      </Button>
    </div>
  );
};
